import { useEffect, useState } from "react";
import { Download, Puzzle, RefreshCw, ArrowRight, Sparkles } from "lucide-react";
import { Card, CardBody, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

export function Install() {
  const { t } = useI18n();
  const [version, setVersion] = useState<string | null>(null);
  useEffect(() => {
    api<{ version: string }>("/extension/version").then((r) => setVersion(r.version)).catch(() => {});
  }, []);
  // URL réelle du site (jamais un placeholder) — fonctionne en dev comme en prod.
  const apiOrigin = typeof window !== "undefined" ? window.location.origin : "https://www.d8a.fr";

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header>
        <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-wide text-muted-fg">
          <Puzzle className="h-3.5 w-3.5" />
          {t("install.kicker") || "Browser extension"}
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("install.title") || "TCGPricer Capture for Chrome"}
        </h1>
        <p className="mt-1 text-sm text-muted-fg">
          {t("install.subtitle") ||
            "Capture prices from Cardmarket and eBay sold listings in one click, identified by Claude vision and pushed to TCGPricer automatically."}
        </p>
      </header>

      <Card>
        <CardBody className="flex flex-wrap items-center justify-between gap-4 p-6">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Sparkles className="h-4 w-4 text-primary" />
              TCGPricer Capture
              {version ? <Badge tone="primary" variant="soft">v{version}</Badge> : null}
            </div>
            <p className="mt-1 text-xs text-muted-fg">
              {t("install.zip_hint") || "Unzip the archive before loading it. The embedded public key keeps your settings across reinstalls."}
            </p>
          </div>
          <a
            href="/extension/tcgpricer-capture.zip"
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-fg shadow-soft transition-colors hover:bg-primary/90"
          >
            <Download className="h-4 w-4" />
            {t("install.download") || "Download ZIP"}
          </a>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <div>
            <CardTitle>{t("install.steps.title") || "Installation steps"}</CardTitle>
            <CardDescription>
              {t("install.steps.help") || "Quick install in Chrome / Brave / Edge."}
            </CardDescription>
          </div>
        </CardHeader>
        <CardBody className="space-y-4 p-6">
          <Step n={1} title={t("install.step1") || "Download the ZIP above"}>
            <Description>
              {t("install.step1.desc") || "Then unzip it to a permanent folder on your machine."}
            </Description>
          </Step>
          <Step n={2} title={t("install.step2") || "Open chrome://extensions"}>
            <Description>
              Open <Kbd>chrome://extensions</Kbd> {t("install.step2.in") || "in"} Chrome / Brave / Edge.
            </Description>
          </Step>
          <Step n={3} title={t("install.step3") || "Toggle Developer mode"}>
            <Description>
              {t("install.step3.desc") || "Top-right corner of the extensions page."}
            </Description>
          </Step>
          <Step n={4} title={t("install.step4") || "Load unpacked"}>
            <Description>
              {t("install.step4.desc") || "Click ‘Load unpacked’ and select the unzipped folder."}
            </Description>
          </Step>
          <Step n={5} title={t("install.step5") || "Configure endpoint and token"}>
            <Description>
              {t("install.step5.desc") || "The Options page opens automatically. Fill in:"}
            </Description>
            <ul className="mt-2 space-y-1 pl-5 text-xs">
              <li>
                <span className="font-medium text-fg">Endpoint API</span>: <Kbd>{apiOrigin}/api/prices/import-html</Kbd>
              </li>
              <li>
                <span className="font-medium text-fg">Import token</span>: {t("install.step5.token") || "the MANUAL_PRICE_IMPORT_TOKEN value on your server."}
              </li>
            </ul>
          </Step>
          <Step n={6} title={t("install.step6") || "Capture a page"}>
            <Description>
              {t("install.step6.desc") || "Open a Cardmarket product page or an eBay sold-listings search and click the TCGPricer icon → Capture this page."}
            </Description>
          </Step>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4 text-muted-fg" />
            {t("install.updates.title") || "Keeping the extension up to date"}
          </CardTitle>
        </CardHeader>
        <CardBody className="space-y-2 p-6 text-sm text-muted-fg">
          <p>
            {t("install.updates.desc") ||
              "When a new version is released, re-download the ZIP, overwrite the unzipped folder, then click the ↻ button on chrome://extensions."}
          </p>
          <a
            href="/extension/tcgpricer-capture.zip"
            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
          >
            {t("install.download_again") || "Download the latest ZIP"}
            <ArrowRight className="h-3 w-3" />
          </a>
        </CardBody>
      </Card>
    </div>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary text-[11px] font-semibold text-primary-fg shadow-soft">
        {n}
      </div>
      <div className="flex-1">
        <div className="text-sm font-medium text-fg">{title}</div>
        <div className="mt-1">{children}</div>
      </div>
    </div>
  );
}

function Description({ children }: { children: React.ReactNode }) {
  return <div className="text-xs leading-relaxed text-muted-fg">{children}</div>;
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-block rounded-sm border border-border bg-surface-2 px-1.5 py-0.5 font-mono text-[10px] text-fg">
      {children}
    </kbd>
  );
}
